import React, { useCallback, useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
  Box,
  Paper,
  TextField,
  Button,
  Typography,
  Alert,
  Grid,
  CircularProgress,
  Chip,
  Stack,
  alpha,
  useTheme,
  InputAdornment,
  ToggleButton,
  ToggleButtonGroup,
} from "@mui/material";
import {
  CheckCircle as CheckCircleIcon,
  Nfc as NfcIcon,
  Payments as PaymentsIcon,
  AccountBalanceWallet as WalletIcon,
  Backspace as BackspaceIcon,
} from "@mui/icons-material";
import { useMutation } from "@tanstack/react-query";
import rechargeAPI from "../../services/rechargeAPI";
import NfcCardScanInput from "../../components/nfc/NfcCardScanInput";
import PageHeader from "../../components/layout/PageHeader";
import { primaryGradient, tokens } from "../../theme";
import { useRBAC } from "../../hooks/useRBAC";

const QUICK_AMOUNTS = [100, 200, 500, 1000, 2000, 5000];

const PAYMENT_METHODS = [
  { value: "cash", label: "Cash" },
  { value: "upi", label: "UPI" },
  { value: "card", label: "Card" },
  { value: "netbanking", label: "Net Banking" },
] as const;

const formatInr = (n: number) =>
  new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    maximumFractionDigits: 2,
  }).format(n);

const StepBadge: React.FC<{ step: number; label: string; active: boolean; done?: boolean }> = ({
  step,
  label,
  active,
  done,
}) => (
  <Stack direction="row" alignItems="center" spacing={1}>
    <Box
      sx={{
        width: 28,
        height: 28,
        borderRadius: "50%",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        fontSize: "0.8125rem",
        fontWeight: 800,
        bgcolor: done ? "success.main" : active ? "primary.main" : "action.selected",
        color: done || active ? "primary.contrastText" : "text.secondary",
      }}
    >
      {done ? "✓" : step}
    </Box>
    <Typography
      variant="subtitle2"
      fontWeight={700}
      color={active ? "text.primary" : "text.secondary"}
    >
      {label}
    </Typography>
  </Stack>
);

const CALC_KEYS = [
  ["1", "2", "3"],
  ["4", "5", "6"],
  ["7", "8", "9"],
  ["C", "0", "."],
] as const;

const appendCalcKey = (prev: string, key: string): string => {
  if (key === "C") return "";
  if (key === "backspace") return prev.slice(0, -1);
  if (key === ".") {
    if (prev.includes(".")) return prev;
    return prev === "" ? "0." : `${prev}.`;
  }
  if (prev === "0" && key !== ".") return key;
  const next = `${prev}${key}`;
  const [, decimals] = next.split(".");
  if (decimals && decimals.length > 2) return prev;
  return next;
};

const AmountKeypad: React.FC<{
  amount: string;
  onAmountChange: (value: string) => void;
  disabled?: boolean;
}> = ({ amount, onAmountChange, disabled }) => {
  const theme = useTheme();

  const pressKey = (key: string) => {
    if (disabled) return;
    onAmountChange(appendCalcKey(amount, key));
  };

  const keyButtonSx = {
    py: 0.65,
    minHeight: 38,
    fontSize: "0.9375rem",
    fontWeight: 700,
    borderRadius: 1.5,
  };

  return (
    <Box>
      <TextField
        fullWidth
        size="small"
        label="Recharge amount"
        value={amount}
        placeholder="0.00"
        disabled={disabled}
        sx={{ mt: 1.5, mb: 1 }}
        InputProps={{
          readOnly: true,
          startAdornment: (
            <InputAdornment position="start">
              <Typography fontWeight={700} color="primary.main" fontSize="0.95rem">
                ₹
              </Typography>
            </InputAdornment>
          ),
          sx: { fontSize: "1.25rem", fontWeight: 700, bgcolor: "action.hover" },
        }}
        helperText="Calculator buttons — NFC reader sirf card scan lai"
      />

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "repeat(3, 1fr)",
          gap: 0.75,
          mb: 0.75,
        }}
      >
        {CALC_KEYS.flat().map((key) => (
          <Button
            key={key}
            variant={key === "C" ? "outlined" : "contained"}
            color={key === "C" ? "error" : "inherit"}
            onClick={() => pressKey(key === "C" ? "C" : key)}
            disabled={disabled}
            sx={{
              ...keyButtonSx,
              ...(key !== "C" && {
                bgcolor: "background.paper",
                color: "text.primary",
                border: `1px solid ${theme.palette.divider}`,
                "&:hover": { bgcolor: alpha(theme.palette.primary.main, 0.08) },
              }),
            }}
          >
            {key}
          </Button>
        ))}
      </Box>

      <Button
        fullWidth
        variant="outlined"
        size="small"
        startIcon={<BackspaceIcon fontSize="small" />}
        onClick={() => pressKey("backspace")}
        disabled={disabled || !amount}
        sx={{ mb: 1.5, py: 0.5, fontWeight: 600, borderRadius: 1.5 }}
      >
        Backspace
      </Button>
    </Box>
  );
};

const SimpleRechargePage: React.FC = () => {
  const theme = useTheme();
  const { canPerformRecharge } = useRBAC();
  const [searchParams] = useSearchParams();

  const [amount, setAmount] = useState(searchParams.get("amount") || "");
  const [paymentMethod, setPaymentMethod] = useState<string>("cash");
  const scanTokenRef = useRef("");
  const rechargeInFlightRef = useRef(false);
  const [scanBuffer, setScanBuffer] = useState("");
  const [displayCardNumber, setDisplayCardNumber] = useState(
    () => searchParams.get("displayNumber")?.trim() || ""
  );
  const [cardBalance, setCardBalance] = useState<number | null>(null);
  const [cardHolderName, setCardHolderName] = useState("");
  const [lastSuccess, setLastSuccess] = useState(false);
  const [alert, setAlert] = useState<{
    type: "success" | "error";
    message: string;
  } | null>(null);

  const parsedAmount = parseFloat(amount);
  const amountReady = !isNaN(parsedAmount) && parsedAmount > 0;

  const resetCardState = () => {
    scanTokenRef.current = "";
    rechargeInFlightRef.current = false;
    setScanBuffer("");
    setDisplayCardNumber("");
    setCardBalance(null);
    setCardHolderName("");
    setLastSuccess(false);
  };

  useEffect(() => {
    const fromUrl = searchParams.get("displayNumber")?.trim();
    if (fromUrl) setDisplayCardNumber(fromUrl);
  }, [searchParams]);

  const rechargeMutation = useMutation({
    mutationFn: ({
      cardNumber: num,
      amount: rechargeAmount,
      paymentMethod: method,
    }: {
      cardNumber: string;
      amount: number;
      paymentMethod: string;
    }) => rechargeAPI.directRecharge(num, rechargeAmount, method),
    onSuccess: (data, vars) => {
      const newBalance = data.newBalance ?? data.nfcCard?.balance ?? null;
      const holder =
        data.holder?.holderName ||
        data.nfcCard?.holder?.holderName ||
        data.cardHolder?.holderName;

      setDisplayCardNumber(data.nfcCard?.displayNumber || displayCardNumber || "");
      scanTokenRef.current = "";
      setScanBuffer("");
      setCardHolderName(holder || "Card recharged");
      if (newBalance != null) setCardBalance(Number(newBalance));
      setLastSuccess(true);
      setAlert({
        type: "success",
        message: `${formatInr(vars.amount)} recharged — new balance ${formatInr(Number(newBalance ?? 0))}`,
      });

      setTimeout(() => {
        setAmount("");
        resetCardState();
        setAlert(null);
      }, 3500);
    },
    onError: (error: unknown) => {
      const msg =
        (error as { response?: { data?: { message?: string } } })?.response?.data
          ?.message || "Recharge failed";
      setAlert({ type: "error", message: msg });
    },
    onSettled: () => {
      rechargeInFlightRef.current = false;
    },
  });

  const processRecharge = useCallback(
    (scannedCard: string) => {
      const num = scannedCard.trim();
      if (!amountReady) {
        setAlert({
          type: "error",
          message: "Pehle amount enter karein, phir card tap karein",
        });
        return;
      }
      if (!num || rechargeInFlightRef.current) return;

      rechargeInFlightRef.current = true;
      setAlert(null);
      setLastSuccess(false);
      rechargeMutation.mutate({
        cardNumber: num,
        amount: parsedAmount,
        paymentMethod,
      });
    },
    [amountReady, parsedAmount, paymentMethod, rechargeMutation]
  );

  const handleScanComplete = (scanned: string) => {
    scanTokenRef.current = scanned;
    setScanBuffer("");
    processRecharge(scanned);
  };

  const busy = rechargeMutation.isPending;

  if (!canPerformRecharge()) {
    return (
      <Box>
        <PageHeader
          title="Quick Recharge"
          subtitle="NFC card top-up"
          breadcrumbs={[
            { label: "Dashboard", to: "/dashboard" },
            { label: "Quick Recharge" },
          ]}
        />
        <Alert severity="warning">
          Recharge is available for Admin and Operator roles only.
        </Alert>
      </Box>
    );
  }

  return (
    <Box>
      <PageHeader
        title="Quick Recharge"
        subtitle="Amount set karein → customer card tap karein — automatic recharge"
        breadcrumbs={[
          { label: "Dashboard", to: "/dashboard" },
          { label: "Quick Recharge" },
        ]}
      />

        {alert && (
          <Alert
            severity={alert.type}
            sx={{ mb: 2, borderRadius: 2 }}
            onClose={() => setAlert(null)}
          >
            {alert.message}
          </Alert>
        )}

        {lastSuccess && cardHolderName && (
          <Paper
            elevation={0}
            sx={{
              mb: 2,
              p: 2.5,
              borderRadius: 2,
              border: "1px solid",
              borderColor: "success.light",
              bgcolor: alpha(theme.palette.success.main, 0.08),
            }}
          >
            <Stack
              direction={{ xs: "column", sm: "row" }}
              spacing={2}
              alignItems={{ xs: "flex-start", sm: "center" }}
            >
              <CheckCircleIcon color="success" sx={{ fontSize: 48 }} />
              <Box flex={1}>
                <Typography variant="overline" color="success.dark" fontWeight={700}>
                  Recharge successful
                </Typography>
                <Typography variant="h6" fontWeight={800}>
                  {cardHolderName}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {displayCardNumber}
                </Typography>
              </Box>
              {cardBalance != null && (
                <Box
                  sx={{
                    px: 2.5,
                    py: 1.5,
                    borderRadius: 2,
                    bgcolor: "background.paper",
                    border: "1px solid",
                    borderColor: "divider",
                    textAlign: { xs: "left", sm: "right" },
                  }}
                >
                  <Typography variant="caption" color="text.secondary">
                    New balance
                  </Typography>
                  <Typography variant="h5" fontWeight={800} color="success.main">
                    {formatInr(cardBalance)}
                  </Typography>
                </Box>
              )}
            </Stack>
          </Paper>
        )}

        <Grid container spacing={2}>
          {/* Step 1 — Amount */}
          <Grid item xs={12} md={5}>
            <Paper
              elevation={0}
              sx={{
                p: 2,
                height: "100%",
                borderRadius: 1,
                border: "1px solid",
                borderColor: "divider",
              }}
            >
              <StepBadge step={1} label="Amount & payment" active={!amountReady} done={amountReady} />

              <AmountKeypad
                amount={amount}
                disabled={busy}
                onAmountChange={(value) => {
                  setAmount(value);
                  resetCardState();
                }}
              />

              <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ mb: 1, display: "block" }}>
                Quick select
              </Typography>
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: "repeat(3, 1fr)",
                  gap: 0.75,
                  mb: 2,
                }}
              >
                {QUICK_AMOUNTS.map((q) => {
                  const selected = amount === String(q);
                  return (
                    <Button
                      key={q}
                      size="small"
                      variant={selected ? "contained" : "outlined"}
                      onClick={() => {
                        setAmount(String(q));
                        resetCardState();
                      }}
                      disabled={busy}
                      sx={{
                        py: 0.75,
                        fontSize: "0.8125rem",
                        fontWeight: 700,
                        borderRadius: 1.5,
                        ...(selected && {
                          background: primaryGradient,
                          boxShadow: `0 2px 8px ${alpha(tokens.colors.primary.main, 0.3)}`,
                        }),
                      }}
                    >
                      ₹{q}
                    </Button>
                  );
                })}
              </Box>

              <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
                <PaymentsIcon fontSize="small" color="action" />
                <Typography variant="caption" color="text.secondary" fontWeight={600}>
                  Payment method
                </Typography>
              </Stack>
              <ToggleButtonGroup
                value={paymentMethod}
                exclusive
                fullWidth
                onChange={(_, v) => v && setPaymentMethod(v)}
                disabled={busy}
                size="small"
                sx={{
                  flexWrap: "wrap",
                  gap: 0.5,
                  "& .MuiToggleButton-root": {
                    flex: "1 1 45%",
                    borderRadius: "8px !important",
                    fontWeight: 600,
                    textTransform: "none",
                    border: `1px solid ${theme.palette.divider} !important`,
                    "&.Mui-selected": {
                      bgcolor: alpha(theme.palette.primary.main, 0.12),
                      color: "primary.main",
                      fontWeight: 700,
                    },
                  },
                }}
              >
                {PAYMENT_METHODS.map((m) => (
                  <ToggleButton key={m.value} value={m.value}>
                    {m.label}
                  </ToggleButton>
                ))}
              </ToggleButtonGroup>

              {amountReady && (
                <Paper
                  elevation={0}
                  sx={{
                    mt: 2,
                    p: 1.5,
                    borderRadius: 1,
                    textAlign: "center",
                    background: primaryGradient,
                    color: "primary.contrastText",
                  }}
                >
                  <Typography variant="caption" sx={{ opacity: 0.9 }}>
                    Ready to charge
                  </Typography>
                  <Typography variant="h5" fontWeight={800} sx={{ letterSpacing: "-0.02em" }}>
                    {formatInr(parsedAmount)}
                  </Typography>
                  <Typography variant="caption" sx={{ opacity: 0.85 }}>
                    Ab card tap karein →
                  </Typography>
                </Paper>
              )}
            </Paper>
          </Grid>

          {/* Step 2 — NFC */}
          <Grid item xs={12} md={7}>
            <Paper
              elevation={0}
              sx={{
                p: 2,
                height: "100%",
                borderRadius: 1,
                border: "2px solid",
                borderColor: amountReady
                  ? alpha(theme.palette.primary.main, 0.4)
                  : "divider",
                bgcolor: amountReady
                  ? alpha(theme.palette.primary.main, 0.02)
                  : "background.paper",
                transition: "border-color 0.3s, background-color 0.3s",
                position: "relative",
                overflow: "hidden",
              }}
            >
              {amountReady && (
                <Box
                  sx={{
                    position: "absolute",
                    top: 0,
                    left: 0,
                    right: 0,
                    height: 4,
                    background: primaryGradient,
                  }}
                />
              )}

              <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
                flexWrap="wrap"
                gap={1}
                sx={{ mb: 2 }}
              >
                <StepBadge
                  step={2}
                  label="Tap NFC card"
                  active={amountReady && !busy}
                  done={lastSuccess}
                />
                {amountReady && (
                  <Chip
                    icon={<NfcIcon />}
                    label="Scanner active"
                    color="primary"
                    size="small"
                    variant="outlined"
                  />
                )}
              </Stack>

              {!amountReady ? (
                <Box
                  sx={{
                    py: 6,
                    px: 2,
                    textAlign: "center",
                    borderRadius: 2,
                    border: "2px dashed",
                    borderColor: "divider",
                    bgcolor: "action.hover",
                  }}
                >
                  <WalletIcon sx={{ fontSize: 56, color: "action.disabled", mb: 1 }} />
                  <Typography variant="h6" fontWeight={700} color="text.secondary">
                    Amount enter karein
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, maxWidth: 280, mx: "auto" }}>
                    Step 1 complete hone ke baad yahan card tap karna shuru ho jayega
                  </Typography>
                </Box>
              ) : busy ? (
                <Box
                  sx={{
                    py: 8,
                    textAlign: "center",
                    borderRadius: 2,
                    border: "2px dashed",
                    borderColor: "primary.light",
                    bgcolor: alpha(theme.palette.primary.main, 0.06),
                  }}
                >
                  <CircularProgress size={56} thickness={4} sx={{ mb: 2 }} />
                  <Typography variant="h5" fontWeight={800}>
                    Recharging…
                  </Typography>
                  <Typography variant="h6" color="primary.main" fontWeight={700}>
                    {formatInr(parsedAmount)}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                    Please wait, card process ho rahi hai
                  </Typography>
                </Box>
              ) : (
                <Box sx={{ "& .MuiPaper-root": { borderRadius: 2 } }}>
                  <NfcCardScanInput
                    value={scanBuffer}
                    onChange={setScanBuffer}
                    onScanComplete={handleScanComplete}
                    hideScannedValue
                    disabled={!amountReady || busy}
                    hint={`Tap card — ${formatInr(parsedAmount)} recharge hoga`}
                    label="NFC card"
                  />
                </Box>
              )}

              <Alert
                severity="info"
                icon={<NfcIcon />}
                sx={{ mt: 2, borderRadius: 2 }}
                variant="outlined"
              >
                USB NFC reader se card tap karein .
              </Alert>
            </Paper>
          </Grid>
        </Grid>
    </Box>
  );
};

export default SimpleRechargePage;
